import { isFunction, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { MediaInfoRawData } from "../../model/MediaInfo.js";
import { AudioInfoRawData } from "../../model/audio/AudioInfo.js";
import { VideoInfoRawData } from "../../model/video/VideoInfo.js";
import { MediaControlOrigin } from "../events/PlayerEvent.js";
import { GenericFormatLabel, ParsedFormat } from "./video/FormatAndSizePreference.js";

export type MediaEventName = keyof {
    timeupdate: boolean,
    progress: boolean,
    pause: boolean,
    play: boolean,
    complete: boolean,
    waiting: boolean,
    stalled: boolean,
    ended: boolean,
    loadedmetadata: boolean,
    volumechange: boolean,
    playing: boolean,
    suspend: boolean,
    seeking: boolean
};


export type MediaResourceStats = {
    loadStartTime: number,
    loadEndTime: number,
}

export interface MediaRef<EL extends HTMLMediaElement, MI extends MediaInfoRawData<any>> {
    element: EL
    info: MI
    executionId: ExecutionId
    ensureResourceLoad(): Promise<void>
}


export interface VideoRef extends MediaRef<HTMLVideoElement, VideoInfoRawData> {

}

export interface AudioRef extends MediaRef<HTMLAudioElement, AudioInfoRawData> {

}

function S4() {
    return Math.floor(Math.random() * 0x10000 /* 65536 */).toString(16);
}

export type ExecutionId = string

let executionCounter = 0;

function getExecutionId(mediaId: string): ExecutionId {
    const uExec = ++executionCounter;
    return `${uExec}-${mediaId}-${S4()}-${S4()}`;
}



export abstract class MediaResource<MediaPlayer, EL extends HTMLMediaElement, MI extends MediaInfoRawData<any>> implements MediaRef<EL, MI> {
    private executionIdInt: ExecutionId;
    private lastOrigin: MediaControlOrigin;

    constructor(
        public readonly player: MediaPlayer,
        public readonly element: EL,
        public readonly listItem: HTMLElement,
        public readonly info: MI,
        protected readonly fixedDuration: number,
        public readonly stats: MediaResourceStats
    ) {
        this.executionIdInt = getExecutionId(info.id);
    }


    protected abstract get internalDuration(): number;
    protected abstract internalPlay(): Promise<any>;
    protected abstract internalRestartOperations(): Promise<any>;
    protected abstract internalResetOperations(): Promise<any>;
    protected abstract internalPause(): Promise<any>;

    abstract get ended(): boolean;
    abstract get currentTime(): number;
    abstract get isPaused(): boolean;

    abstract destroy(): void;
    abstract seek(time: number): Promise<any>;
    abstract reloadSource(keepCurrentTime: boolean): Promise<void>

    abstract show(): void;
    abstract hide(): void;
    abstract muted(muted?: boolean): boolean;
    abstract volume(volume?: number): number;
    abstract autoplay(autoplay?: boolean): boolean;
    abstract loop(loopEnabled?: boolean): boolean;

    abstract beforeUnload(): Promise<void>;
    abstract on(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void;
    abstract off(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void;

    get duration(): number {
        if (this.fixedDuration > 0) return this.fixedDuration;
        else return this.internalDuration;
    }

    get executionId(): ExecutionId {
        return this.executionIdInt;
    }

    ensureResourceLoad(): Promise<void> {
        return Promise.resolve();
    }

    play(origin: MediaControlOrigin): Promise<any> {
        this.lastOrigin = origin;
        return this.internalPlay();
    }

    restart(): Promise<any> {
        this.executionIdInt = getExecutionId(this.info.id);
        return this.internalRestartOperations();
    }

    reset(): Promise<any> {
        this.executionIdInt = getExecutionId(this.info.id);
        return this.internalResetOperations();
    }

    fetchLastMediaControlOrigin(): MediaControlOrigin {
        const fetched = this.lastOrigin;
        this.lastOrigin = null;
        return fetched ? fetched : MediaControlOrigin.NON_USER;
    }

    pause(origin: MediaControlOrigin): Promise<any> {
        this.lastOrigin = origin;
        return this.internalPause();
    }

    once(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
        const nuListener = (evt: Event) => {
            if (isObject(listener) && ("handleEvent" in listener) && isFunction(listener.handleEvent)) {
                listener.handleEvent(evt);
            }
            else if (isFunction(listener)) {
                listener(evt);
            }
            this.off(eventName, nuListener);

        }
        this.on(eventName, nuListener);
    }

    get metadataPromise(): Promise<void> {
        return this.element.readyState >= 1 ? Promise.resolve() :
            new Promise((resolve) => {
                this.once("loadedmetadata", () => resolve());
            });
    }
}




export abstract class VideoResource<MediaPlayer> extends MediaResource<MediaPlayer, HTMLVideoElement, VideoInfoRawData> {
    constructor(
        public readonly player: MediaPlayer,
        public readonly element: HTMLVideoElement,
        public readonly listItem: HTMLElement,
        public readonly info: VideoInfoRawData,
        protected readonly fixedDuration: number,
        public readonly stats: MediaResourceStats,
        private readonly availQual: ParsedFormat[]
    ) {
        super(player, element, listItem, info, fixedDuration, stats)
    }

    toggleFullscreen(fullscreen: boolean): boolean {
        return fullscreen
    }

    getAvailableQualities(): ParsedFormat[] {
        return this.availQual;
    }

    abstract getCurrentFormat(): ParsedFormat;

    abstract switchQuality(formatLabel: GenericFormatLabel): Promise<ParsedFormat>;
}
export abstract class AudioResource<MediaPlayer> extends MediaResource<MediaPlayer, HTMLAudioElement, AudioInfoRawData> {
    constructor(
        public readonly player: MediaPlayer,
        public readonly element: HTMLAudioElement,
        public readonly listItem: HTMLElement,
        public readonly info: AudioInfoRawData,
        protected readonly fixedDuration: number,
        public readonly stats: MediaResourceStats,
    ) {
        super(player, element, listItem, info, fixedDuration, stats)
    }

    abstract getCurrentSource(): string;

}